import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import Spinner from '../components/Spinner';
import { FiShoppingBag, FiPhone, FiMapPin, FiShoppingCart, FiX, FiPlus, FiMinus, FiTrash2, FiUser, FiCreditCard, FiSearch, FiFilter, FiArrowRight, FiInstagram, FiFacebook, FiGlobe, FiShare2, FiMessageCircle, FiInfo, FiArrowUp, FiArrowDown, FiCheck, FiChevronLeft, FiChevronRight, FiStar, FiPlusCircle } from 'react-icons/fi';
import logo from '../assets/logo.png';

const getProductColor = (name) => {
  const colors = ['bg-red-100 text-red-600', 'bg-orange-100 text-orange-600', 'bg-amber-100 text-amber-600', 'bg-yellow-100 text-yellow-600', 'bg-lime-100 text-lime-600', 'bg-green-100 text-green-600', 'bg-emerald-100 text-emerald-600', 'bg-teal-100 text-teal-600', 'bg-cyan-100 text-cyan-600', 'bg-sky-100 text-sky-600', 'bg-blue-100 text-blue-600', 'bg-indigo-100 text-indigo-600', 'bg-violet-100 text-violet-600', 'bg-purple-100 text-purple-600', 'bg-fuchsia-100 text-fuchsia-600', 'bg-pink-100 text-pink-600', 'bg-rose-100 text-rose-600'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const VariantSelectionModal = ({ isOpen, onClose, product, onSelectVariant, primaryColor }) => {
  if (!isOpen || !product || !product.variants || product.variants.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-center items-center p-4 animate-fade-in" onClick={onClose}>
      <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Choisir une option</h3>
          <button onClick={onClose}><FiX size={24} /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">Produit : <span className="font-bold">{product.name}</span></p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {product.variants.map(variant => (
            <button 
              key={variant.name}
              onClick={() => onSelectVariant(variant)}
              disabled={variant.quantity <= 0}
              className="p-4 border-2 rounded-xl text-center font-bold hover:border-brand-blue focus:border-brand-blue outline-none transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:border-gray-200"
              style={{'--tw-ring-color': primaryColor, '--tw-border-color': primaryColor}}
            >
              <span className="block">{variant.name}</span>
              <span className="text-xs text-gray-400">({variant.quantity} restants)</span>
            </button>
          ))}
        </div>
        <div className="mt-6 text-right">
          <button onClick={onClose} className="bg-gray-100 text-gray-800 font-bold py-2 px-5 rounded-lg hover:bg-gray-200 transition-colors">Annuler</button>
        </div>
      </div>
    </div>
  );
};

const ProductDetailModal = ({ isOpen, onClose, product, onAddToCart, allProducts, onProductSelect, primaryColor }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Reset index when product changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product]);

  const similarProducts = useMemo(() => {
    if (!product || !allProducts) return [];
    return allProducts
      .filter(p => p.category === product.category && p._id !== product._id)
      .slice(0, 3);
  }, [product, allProducts]);

  const images = useMemo(() => {
    if (!product) return [];
    if (product.images && product.images.length > 0) return product.images;
    if (product.imageUrl) return [product.imageUrl];
    return [];
  }, [product]);

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex justify-center items-center p-4 animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col transform transition-all scale-100" onClick={e => e.stopPropagation()}>
        <div className="relative h-72 bg-gray-50 flex-shrink-0 group">
           {images.length > 0 ? (
             <>
               <img src={images[currentImageIndex]} alt={product.name} className="w-full h-full object-contain transition-opacity duration-300" />
               {images.length > 1 && (
                 <>
                   <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full hover:bg-white shadow-md text-gray-800 opacity-0 group-hover:opacity-100 transition-opacity"><FiChevronLeft size={20} /></button>
                   <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full hover:bg-white shadow-md text-gray-800 opacity-0 group-hover:opacity-100 transition-opacity"><FiChevronRight size={20} /></button>
                   <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                     {images.map((_, idx) => (
                       <div key={idx} className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex ? '' : 'bg-white/60'}`} style={idx === currentImageIndex ? { backgroundColor: primaryColor } : {}} />
                     ))}
                   </div>
                 </>
               )}
             </>
           ) : (
             <div className={`w-full h-full flex items-center justify-center text-7xl font-bold ${getProductColor(product.name)}`}>
               {product.name.substring(0, 2).toUpperCase()}
             </div>
           )}
           <button onClick={onClose} className="absolute top-4 right-4 bg-white/90 backdrop-blur p-2 rounded-full hover:bg-white transition-colors text-gray-800 shadow-sm z-10"><FiX size={24} /></button>
        </div>
        <div className="p-6 overflow-y-auto">
          <div className="flex justify-between items-start mb-2">
             <span className="text-xs font-bold text-brand-blue bg-blue-50 px-2 py-1 rounded-md uppercase tracking-wider">{product.category || 'Autres'}</span>
             {!product.isService && (
                product.quantity > 0 ? (
                    <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-md flex items-center"><span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5"></span>En stock</span>
                ) : (
                    <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded-md">Épuisé</span>
                )
             )}
             {product.isService && (
                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-md flex items-center"><span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-1.5"></span>Disponible</span>
             )}
          </div>
          
          <h3 className="text-2xl font-bold text-gray-900 mb-2 leading-tight">{product.name}</h3>
          <p className="text-3xl font-extrabold text-gray-900 mb-4">{product.salePrice.toLocaleString('fr-FR')} <span className="text-sm text-gray-500 font-normal">CFA</span></p>
          
          <div className="prose prose-sm text-gray-600 mb-8">
            <p>{product.description || "Aucune description détaillée disponible pour ce produit."}</p>
          </div>

          <button 
            onClick={() => { onAddToCart(product); onClose(); }}
            disabled={!product.isService && product.quantity <= 0}
            className="w-full text-white font-bold py-4 rounded-2xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-8 transform active:scale-[0.98]"
            style={{ backgroundColor: primaryColor }}
          >
            {product.isService || product.quantity > 0 ? <><FiPlusCircle /> Ajouter au panier</> : 'Rupture de stock'}
          </button>

          {similarProducts.length > 0 && (
            <div>
              <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wide">Cela pourrait vous plaire</h4>
              <div className="grid grid-cols-3 gap-3">
                {similarProducts.map(p => (
                  <div key={p._id} className="group cursor-pointer" onClick={() => onProductSelect(p)}>
                    <div className={`w-full aspect-square rounded-xl mb-2 flex items-center justify-center text-sm font-bold overflow-hidden ${p.imageUrl || (p.images && p.images.length > 0) ? '' : getProductColor(p.name)}`}>
                        {p.images && p.images.length > 0 ? (
                            <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"/>
                        ) : p.imageUrl ? (
                            <img src={p.imageUrl} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"/>
                        ) : (
                            p.name.substring(0, 2).toUpperCase()
                        )}
                    </div>
                    <p className="text-xs font-bold text-gray-800 truncate">{p.name}</p>
                    <p className="text-xs text-gray-500">{p.salePrice.toLocaleString()} F</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PublicCartModal = ({ isOpen, onClose, cartItems, onUpdateQuantity, onCheckout, shopPhone, paymentPhone, primaryColor }) => {
  const [customerDetails, setCustomerDetails] = useState({ name: '', phone: '', address: '' });
  const [paymentMethod, setPaymentMethod] = useState('cash'); // 'cash', 'wave', 'om'
  const total = cartItems.reduce((acc, item) => acc + item.salePrice * item.quantity, 0);

  const handleDetailChange = (e) => {
    setCustomerDetails({ ...customerDetails, [e.target.name]: e.target.value });
  };

  const handleConfirmCheckout = () => {
    if (!customerDetails.name || !customerDetails.phone) {
      toast.error("Veuillez renseigner votre nom et votre téléphone.");
      return;
    }
    onCheckout({ ...customerDetails, paymentMethod });
  };

  const handleWhatsAppCheckout = () => {
    if (!shopPhone) {
        toast.error("Le numéro du vendeur n'est pas disponible.");
        return;
    }
    if (!customerDetails.name) {
        toast.error("Veuillez indiquer votre nom.");
        return;
    }
    
    let message = `*Nouvelle Commande via Jaapand-e* 🛍️\n\n`;
    message += `👤 *Client:* ${customerDetails.name}\n`;
    if(customerDetails.phone) message += `📞 *Tél:* ${customerDetails.phone}\n`;
    if(customerDetails.address) message += `📍 *Adresse:* ${customerDetails.address}\n\n`;
    
    message += `*Commande:*\n`;
    cartItems.forEach(item => {
        message += `- ${item.quantity}x ${item.name} (${(item.salePrice).toLocaleString()} F)\n`;
    });
    
    const total = cartItems.reduce((acc, item) => acc + item.salePrice * item.quantity, 0);
    message += `\n💰 *Total: ${total.toLocaleString()} CFA*`;
    
    const url = `https://wa.me/${shopPhone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} 
        onClick={onClose}
      />
      <div className={`fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'} rounded-l-2xl`}>
        <div className="flex justify-between items-center p-6 border-b bg-white">
          <h3 className="text-2xl font-bold text-gray-900 flex items-center font-poppins">Mon Panier <span className="ml-2 text-sm font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{cartItems.length} articles</span></h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><FiX size={24} /></button>
        </div>

        <div className="p-6 flex-grow overflow-y-auto bg-gray-50">
          {cartItems.length > 0 ? (
            <div className="space-y-8">
              <div className="space-y-4">
              {cartItems.map(item => (
                <div key={item._id} className="flex gap-4 p-4 bg-white rounded-2xl shadow-sm border border-gray-100">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.name} className="w-20 h-20 rounded-xl object-cover border bg-gray-50" />
                  ) : (
                    <div className={`w-20 h-20 rounded-xl flex items-center justify-center text-xl font-bold ${getProductColor(item.name)}`}>
                      {item.name.substring(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-grow flex flex-col justify-between">
                    <div>
                        <p className="font-bold text-gray-900 line-clamp-1">{item.name}</p>
                        <p className="text-gray-500 font-medium text-sm mt-0.5">{item.salePrice.toLocaleString('fr-FR')} CFA</p>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-3 bg-gray-100 rounded-lg p-1">
                            <button onClick={() => onUpdateQuantity(item._id, -1)} className="w-7 h-7 flex items-center justify-center bg-white rounded-md shadow-sm hover:text-brand-blue transition-colors"><FiMinus size={14} /></button>
                            <span className="font-bold text-sm w-4 text-center">{item.quantity}</span>
                            <button onClick={() => onUpdateQuantity(item._id, 1)} className="w-7 h-7 flex items-center justify-center bg-white rounded-md shadow-sm hover:text-brand-blue transition-colors"><FiPlus size={14} /></button>
                        </div>
                        <button onClick={() => onUpdateQuantity(item._id, -item.quantity)} className="text-gray-400 hover:text-red-500 p-2 transition-colors"><FiTrash2 size={18} /></button>
                    </div>
                  </div>
                </div>
              ))}
              </div>
              
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center text-sm uppercase tracking-wide"><FiUser className="mr-2"/> Vos coordonnées</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Nom complet</label>
                    <input name="name" value={customerDetails.name} onChange={handleDetailChange} placeholder="Ex: Moussa Diop" className="p-3 border border-gray-200 bg-gray-50 rounded-xl w-full focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Téléphone</label>
                    <input name="phone" value={customerDetails.phone} onChange={handleDetailChange} placeholder="Ex: 77 123 45 67" className="p-3 border border-gray-200 bg-gray-50 rounded-xl w-full focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Adresse de livraison</label>
                    <textarea name="address" value={customerDetails.address} onChange={handleDetailChange} placeholder="Quartier, indications..." className="p-3 border border-gray-200 bg-gray-50 rounded-xl w-full focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-sm" rows="2" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center text-sm uppercase tracking-wide"><FiCreditCard className="mr-2"/> Paiement</h4>
                <div className="grid grid-cols-3 gap-3">
                    <button 
                        onClick={() => setPaymentMethod('cash')}
                        className={`p-3 rounded-xl border-2 flex flex-col items-center justify-center transition-all ${paymentMethod === 'cash' ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-100 hover:border-gray-300 text-gray-600'}`}
                    >
                        <span className="text-2xl mb-1">💵</span>
                        <span className="text-xs font-bold text-center">Cash</span>
                    </button>
                    <button 
                        onClick={() => setPaymentMethod('wave')}
                        className={`p-3 rounded-xl border-2 flex flex-col items-center justify-center transition-all ${paymentMethod === 'wave' ? 'border-[#1DC0F1] bg-[#1DC0F1]/10 text-[#1DC0F1]' : 'border-gray-100 hover:border-gray-300 text-gray-600'}`}
                    >
                        <img src="/wave_logo.jfif" alt="Wave" className="h-6 w-6 object-contain mb-1 rounded-full" />
                        <span className="text-xs font-bold text-center">Wave</span>
                    </button>
                    <button 
                        onClick={() => setPaymentMethod('om')}
                        className={`p-3 rounded-xl border-2 flex flex-col items-center justify-center transition-all ${paymentMethod === 'om' ? 'border-[#FF7900] bg-[#FF7900]/10 text-[#FF7900]' : 'border-gray-100 hover:border-gray-300 text-gray-600'}`}
                    >
                        <img src="/om_logo.png" alt="Orange Money" className="h-6 w-6 object-contain mb-1 rounded-full" />
                        <span className="text-xs font-bold text-center">OM</span>
                    </button>
                </div>
                
                {paymentMethod !== 'cash' && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-xl text-sm text-blue-800 border border-blue-100">
                        <p className="font-bold mb-1">Instructions :</p>
                        <p>Envoyez <span className="font-bold">{total.toLocaleString('fr-FR')} CFA</span> au numéro ci-dessous via {paymentMethod === 'wave' ? 'Wave' : 'Orange Money'}.</p>
                        <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-blue-100 mt-3 shadow-sm">
                            <span className="font-mono font-bold text-lg tracking-wider">{paymentPhone || shopPhone || "Numéro non disponible"}</span>
                            <button className="text-xs bg-blue-100 text-blue-700 px-3 py-1.5 rounded-md hover:bg-blue-200 font-bold transition-colors" onClick={() => {navigator.clipboard.writeText(paymentPhone || shopPhone); toast.success("Copié !")}} >Copier</button>
                        </div>
                    </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                    <FiShoppingCart size={40} className="opacity-30"/>
                </div>
                <p className="text-lg font-medium text-gray-500">Votre panier est vide.</p>
                <button onClick={onClose} className="mt-6 px-6 py-3 text-white rounded-full font-bold transition-colors" style={{ backgroundColor: primaryColor }}>Commencer mes achats</button>
            </div>
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="p-6 bg-white border-t shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.1)]">
            <div className="flex justify-between items-center mb-6">
              <span className="text-gray-500 font-medium">Total à payer</span>
              <span className="text-3xl font-extrabold text-gray-900">{total.toLocaleString('fr-FR')} <span className="text-sm font-normal text-gray-500">CFA</span></span>
            </div>
            <div className="flex flex-col gap-3">
                <button onClick={handleConfirmCheckout} className="w-full text-white font-bold py-4 rounded-xl transition-all shadow-lg hover:shadow-xl transform active:scale-[0.98]" style={{ backgroundColor: primaryColor }}>
                Confirmer la commande
                </button>
                <button onClick={handleWhatsAppCheckout} className="w-full bg-green-50 text-green-700 border border-green-200 font-bold py-4 rounded-xl hover:bg-green-100 transition-all flex items-center justify-center gap-2">
                <FiMessageCircle /> Commander sur WhatsApp
                </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const PublicShop = () => {
  const { shopSlug } = useParams();
  const [shopData, setShopData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Tout');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sortOrder, setSortOrder] = useState('default'); // default, asc, desc
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const [productForVariantSelection, setProductForVariantSelection] = useState(null);

  useEffect(() => {
    const fetchShopData = async () => {
      try {
        const response = await api.get(`/public/shop/${shopSlug}`);
        setShopData(response.data);
      } catch (err) {
        setError("Boutique non trouvée ou indisponible.");
        console.error("Failed to fetch shop data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchShopData();
  }, [shopSlug]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Réinitialiser la page quand on change de filtre
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchTerm, sortOrder]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShareShop = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shopData?.shop?.shopName || 'Boutique en ligne',
          text: `Découvrez les produits de ${shopData?.shop?.shopName} sur Jaapand-e !`,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Partage annulé');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Lien de la boutique copié !");
    }
  };

  const addItemToCart = (product) => {
    setCartItems(prevCart => {
      const existingItem = prevCart.find(item => item._id === product._id);
      if (existingItem) {
        if (!product.isService && existingItem.quantity >= product.quantity) {
          toast.warn(`Stock maximum atteint pour "${product.name}".`);
          return prevCart;
        }
        return prevCart.map(item =>
          item._id === product._id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      if (!product.isService && product.quantity < 1) {
        toast.error(`"${product.name}" est en rupture de stock.`);
        return prevCart;
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
    toast.success(`${product.name} ajouté au panier !`);
  };

  const handleAddToCart = (product) => {
    // Si le produit a des variantes, on ouvre le modal de sélection
    if (product.variants && product.variants.length > 0) {
      setProductForVariantSelection(product);
      setIsVariantModalOpen(true);
    } else {
      // Sinon, on l'ajoute directement
      addItemToCart(product);
    }
  };

  const handleSelectVariant = (variant) => {
    const productWithVariant = {
      ...productForVariantSelection,
      _id: `${productForVariantSelection._id}-${variant.name}`, // ID unique pour la variante
      name: `${productForVariantSelection.name} (${variant.name})`,
      quantity: variant.quantity, // Stock de la variante
      variantName: variant.name,
      originalProductId: productForVariantSelection._id
    };
    addItemToCart(productWithVariant);
    setIsVariantModalOpen(false);
  };

  const handleUpdateQuantity = (productId, amount) => {
    setCartItems(prevCart => {
      const itemToUpdate = prevCart.find(item => item._id === productId);
      if (!itemToUpdate) return prevCart;

      const productFromStore = products.find(p => p._id === productId);
      if (!productFromStore) return prevCart; // Should not happen

      const newQuantity = itemToUpdate.quantity + amount;
      if (!itemToUpdate.isService && newQuantity > productFromStore.quantity) { 
        toast.warn(`Stock insuffisant. Disponible : ${productFromStore.quantity}`);
        return prevCart;
      }
      if (newQuantity <= 0) return prevCart.filter(item => item._id !== productId);
      return prevCart.map(item => item._id === productId ? { ...item, quantity: newQuantity } : item);
    });
  };

  const handleCheckout = async (customerDetails) => {
    toast.info("Envoi de votre commande...");
    try {
      const orderPayload = {
        customer: { name: customerDetails.name, phone: customerDetails.phone, address: customerDetails.address },
        paymentMethod: customerDetails.paymentMethod,
        items: cartItems.map(item => ({ productId: item._id, quantity: item.quantity, unitPrice: item.salePrice })),
        shopId: shopData.shop._id
      };
      await api.post(`/public/shop/${shopSlug}/order`, orderPayload);
      toast.success("Commande passée avec succès ! Le vendeur vous contactera bientôt.");
      setCartItems([]);
      setIsCartOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur lors de la commande.");
    }
  };

  const products = shopData?.products || [];
  const shop = shopData?.shop;
  const primaryColor = shop?.primaryColor || '#10B981';
  
  const categories = useMemo(() => {
    const cats = ['Tout'];
    const counts = { 'Tout': products.length };
    products.forEach(p => {
        const c = p.category || 'Autres';
        if (!cats.includes(c)) cats.push(c);
        counts[c] = (counts[c] || 0) + 1;
    });
    return { list: cats, counts };
  }, [products]);

  const categoryChunks = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < categories.list.length; i += 5) {
        chunks.push(categories.list.slice(i, i + 5));
    }
    return chunks;
  }, [categories.list]);

  const filteredProducts = useMemo(() => {
    let res = products.filter(product => {
        const matchesCategory = selectedCategory === 'Tout' || (product.category || 'Autres') === selectedCategory;
        const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    if (sortOrder === 'asc') res.sort((a, b) => a.salePrice - b.salePrice);
    if (sortOrder === 'desc') res.sort((a, b) => b.salePrice - a.salePrice);
    return res;
  }, [products, selectedCategory, searchTerm, sortOrder]);

  const ITEMS_PER_PAGE = 15;
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return <Spinner message="Chargement de la boutique..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-center p-4">
        <img src={logo} alt="Jaapand-e" className="h-12 mb-6 opacity-50 grayscale" />
        <h2 className="text-2xl font-bold text-gray-800">{error}</h2>
        <p className="text-gray-500 mt-2">Le lien est peut-être incorrect ou le propriétaire n'a pas activé sa boutique en ligne.</p>
        <Link to="/" className="mt-6 text-brand-blue font-bold hover:underline">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen font-sans flex flex-col">
      {/* Floating Cart Button */}
      {cartItems.length > 0 && (
        <button
          onClick={() => setIsCartOpen(true)}
          className="fixed bottom-6 right-6 text-white w-16 h-16 rounded-full shadow-2xl flex items-center justify-center z-40 transform hover:scale-110 transition-all duration-300 border-4 border-white"
          style={{ backgroundColor: primaryColor }}
        >
          <FiShoppingCart size={24} />
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center border-2 border-white">
            {cartItems.reduce((acc, item) => acc + item.quantity, 0)}
          </span>
        </button>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-24 right-6 bg-white text-gray-900 p-3 rounded-full shadow-lg border border-gray-100 z-40 hover:bg-gray-50 transition-all transform hover:scale-110"
          title="Retour en haut"
        >
          <FiArrowUp size={20} />
        </button>
      )}

      {/* Cart Modal */}
      <PublicCartModal isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} cartItems={cartItems} onUpdateQuantity={handleUpdateQuantity} onCheckout={handleCheckout} shopPhone={shop.phone} paymentPhone={shop.paymentPhone} primaryColor={primaryColor} />
      
      {/* Product Detail Modal */}
      <ProductDetailModal isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} product={selectedProduct} onAddToCart={handleAddToCart} allProducts={products} onProductSelect={setSelectedProduct} primaryColor={primaryColor} />

      <VariantSelectionModal 
        isOpen={isVariantModalOpen} 
        onClose={() => setIsVariantModalOpen(false)} 
        product={productForVariantSelection} 
        onSelectVariant={handleSelectVariant} 
        primaryColor={primaryColor}
      />

      {/* Announcement Bar */}
      <div className="text-white text-xs font-bold py-2 text-center px-4 tracking-wide shadow-sm relative z-20" style={{ backgroundColor: primaryColor }}>
        🚀 Livraison rapide partout au Sénégal ! Commandez maintenant.
      </div>

      {/* New Premium Header */}
      <div className="relative bg-gray-900 text-white pt-12 pb-20 px-4 sm:px-6 overflow-hidden">
        {/* Background Image if available */}
        {shop.bannerUrl ? (
            <div className="absolute inset-0 z-0">
                <img src={shop.bannerUrl} alt="Bannière" className="w-full h-full object-cover opacity-60" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent"></div>
            </div>
        ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-[#1a202c] to-black z-0">
                {/* Background Pattern & Blobs (only if no banner) */}
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]"></div>
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
                <div className="absolute top-0 left-0 -mt-20 -ml-20 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
                <div className="absolute bottom-0 left-20 -mb-20 w-80 h-80 bg-green-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
            </div>
        )}

        <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-gray-50 to-transparent z-0"></div>
        
        <div className="container mx-auto relative z-10 text-center">
            <div className="w-24 h-24 bg-white text-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl text-4xl font-bold border-4 border-white/20 transform hover:scale-105 transition-transform duration-300 overflow-hidden">
                {shop.logoUrl ? (
                    <img src={shop.logoUrl} alt={shop.shopName} className="w-full h-full object-cover" />
                ) : (
                    shop.shopName.substring(0, 1).toUpperCase()
                )}
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold font-poppins mb-3 tracking-tight drop-shadow-lg">{shop.shopName}</h1>
            <div className="flex flex-wrap justify-center gap-4 text-gray-300 text-sm mb-8 font-medium">
                {shop.address && <span className="flex items-center bg-black/30 px-3 py-1.5 rounded-lg backdrop-blur-md border border-white/10 hover:bg-black/40 transition-colors"><FiMapPin className="mr-2 text-brand-green" /> {shop.address}</span>}
                {shop.phone && <span className="flex items-center bg-black/30 px-3 py-1.5 rounded-lg backdrop-blur-md border border-white/10 hover:bg-black/40 transition-colors"><FiPhone className="mr-2 text-brand-blue" /> {shop.phone}</span>}
            </div>
            <div className="flex flex-wrap justify-center gap-4">
                <button onClick={handleShareShop} className="bg-white text-gray-900 hover:bg-gray-100 px-6 py-3 rounded-full font-bold transition-all flex items-center gap-2 shadow-lg transform hover:-translate-y-1 text-sm">
                    <FiShare2 /> Partager
                </button>
                {shop.phone && (
                    <a href={`https://wa.me/${shop.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] hover:bg-[#20bd5a] text-white px-6 py-3 rounded-full font-bold transition-all flex items-center gap-2 shadow-lg shadow-green-500/30 transform hover:-translate-y-1 text-sm">
                        <FiMessageCircle size={20} /> WhatsApp
                    </a>
                )}
                {shop.instagramLink && (
                    <a href={shop.instagramLink} target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white px-6 py-3 rounded-full font-bold transition-all flex items-center gap-2 shadow-lg transform hover:-translate-y-1 text-sm">
                        <FiInstagram size={20} /> Instagram
                    </a>
                )}
            </div>
        </div>
      </div>

      {/* Sticky Search & Filter Bar */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-gray-200 py-3 px-4 shadow-sm transition-all">
        <div className="container mx-auto flex flex-col md:flex-row gap-4 items-center justify-between">
             {/* Categories */}
             <div className="flex-grow w-full md:w-auto overflow-x-auto no-scrollbar order-2 md:order-1 pb-1 md:pb-0">
                <div className="flex gap-2">
                    {categories.list.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setSelectedCategory(cat)}
                            className={`px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all duration-300 ${selectedCategory === cat ? 'text-white shadow-md transform scale-105' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            style={selectedCategory === cat ? { backgroundColor: primaryColor } : {}}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* Search & Sort */}
            <div className="flex items-center gap-3 w-full md:w-auto order-1 md:order-2">
                <div className="relative flex-grow md:w-64 group">
                    <FiSearch className="absolute top-1/2 left-4 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-blue transition-colors" />
                    <input 
                        type="text" 
                        placeholder="Rechercher un produit..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-11 pr-4 py-2.5 bg-gray-100 border-transparent focus:bg-white border focus:border-gray-300 rounded-xl focus:ring-0 outline-none transition-all text-sm font-medium"
                    />
                </div>
                <button 
                    onClick={() => setSortOrder(prev => prev === 'default' ? 'asc' : prev === 'asc' ? 'desc' : 'default')}
                    className={`p-2.5 rounded-full transition-all shadow-sm flex-shrink-0 ${sortOrder !== 'default' ? 'text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
                    style={sortOrder !== 'default' ? { backgroundColor: primaryColor } : {}}
                    title="Trier par prix"
                >
                    {sortOrder === 'asc' ? <FiArrowUp size={18} /> : sortOrder === 'desc' ? <FiArrowDown size={18} /> : <FiFilter size={18} />}
                </button>
            </div>
        </div>
      </div>

      {/* Product Grid */}
      <div className="container mx-auto px-4 py-8 min-h-[60vh] flex-grow -mt-2">
        {filteredProducts.length > 0 ? (
        <>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
            {paginatedProducts.map(product => {
            const isOutOfStock = !product.isService && (product.quantity || 0) <= 0;
            return (
                <div key={product._id} onClick={() => setSelectedProduct(product)} className="bg-white rounded-2xl p-3 shadow-sm hover:shadow-xl transition-all duration-300 group flex flex-col border border-gray-100 cursor-pointer h-full relative overflow-hidden">
                    <div className="relative aspect-square bg-gray-50 rounded-xl overflow-hidden mb-3">
                            {product.images && product.images.length > 0 ? (
                                <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                            ) : product.imageUrl ? (
                                <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                            ) : (
                                <div className={`w-full h-full flex items-center justify-center text-4xl font-bold ${getProductColor(product.name)}`}>
                                    {product.name.substring(0, 2).toUpperCase()}
                                </div>
                            )}
                            {isOutOfStock && (
                                <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-10">
                                    <span className="bg-gray-900 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">Épuisé</span>
                                </div>
                            )}
                            {product.isService && (
                                <div className="absolute top-2 right-2 z-20">
                                    <span className="bg-blue-100 text-blue-800 text-[10px] font-bold px-2 py-1 rounded-md border border-blue-200 shadow-sm backdrop-blur-md">Service</span>
                                </div>
                            )}
                            {!isOutOfStock && (
                                <button 
                                    onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}
                                    className="absolute bottom-2 right-2 w-9 h-9 bg-white text-gray-900 rounded-full shadow-md flex items-center justify-center hover:bg-brand-blue hover:text-white transition-all z-20 active:scale-90"
                                    title="Ajouter au panier"
                                >
                                    <FiPlusCircle size={18} />
                                </button>
                            )}
                    </div>
                    <div className="flex flex-col flex-grow">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{product.category || 'Autres'}</p>
                        <h3 className="font-bold text-gray-900 line-clamp-2 text-sm mb-1 flex-grow leading-snug group-hover:text-brand-blue transition-colors">{product.name}</h3>
                        <div className="flex items-end justify-between mt-1">
                            <p className="text-lg font-extrabold text-gray-900">{product.salePrice.toLocaleString('fr-FR')} <span className="text-xs font-normal text-gray-400">F</span></p>
                        </div>
                    </div>
                </div>
            );
            })}
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
            <div className="flex justify-center items-center mt-12 gap-2">
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-3 rounded-full transition-all border ${currentPage === 1 ? 'text-gray-300 border-gray-100 cursor-not-allowed' : 'text-gray-600 border-gray-200 hover:bg-gray-100 hover:text-brand-blue hover:border-brand-blue'}`}
                >
                    <FiChevronLeft size={20} />
                </button>
                
                <div className="flex gap-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                         if (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                         ) {
                            return (
                                <button
                                    key={page}
                                    onClick={() => handlePageChange(page)}
                                    className={`w-10 h-10 rounded-full font-bold text-sm transition-all flex items-center justify-center ${currentPage === page ? 'text-white shadow-lg transform scale-110' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200 hover:border-gray-300'}`}
                                    style={currentPage === page ? { backgroundColor: primaryColor } : {}}
                                >
                                    {page}
                                </button>
                            );
                         } else if (page === currentPage - 2 || page === currentPage + 2) {
                            return <span key={page} className="w-10 h-10 flex items-center justify-center text-gray-400 font-bold">...</span>;
                         }
                         return null;
                    })}
                </div>

                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-3 rounded-full transition-all border ${currentPage === totalPages ? 'text-gray-300 border-gray-100 cursor-not-allowed' : 'text-gray-600 border-gray-200 hover:bg-gray-100 hover:text-brand-blue hover:border-brand-blue'}`}
                >
                    <FiChevronRight size={20} />
                </button>
            </div>
        )}
        </>
        ) : (
            <div className="text-center py-24 flex flex-col items-center">
                    <div className="bg-white w-24 h-24 rounded-full flex items-center justify-center mb-6 shadow-sm border border-gray-100">
                        <FiSearch className="text-gray-300" size={40} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 font-poppins">Aucun produit trouvé</h3>
                    <p className="text-gray-500 mt-2 max-w-xs mx-auto">Essayez de changer de catégorie ou de modifier votre recherche.</p>
                    <button onClick={() => {setSelectedCategory('Tout'); setSearchTerm('')}} className="mt-8 px-8 py-3 text-white rounded-full font-bold transition-colors shadow-lg" style={{ backgroundColor: primaryColor }}>Voir tous les produits</button>
            </div>
        )}
      </div>

      <footer className="bg-gray-50 border-t border-gray-200 pt-16 pb-12 mt-auto">
        <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
                {/* Shop Info */}
                <div className="lg:col-span-1">
                    <h3 className="text-2xl font-extrabold text-gray-900 mb-6 flex items-center tracking-tight">
                        <div className="w-10 h-10 bg-gray-900 text-white rounded-lg flex items-center justify-center mr-3 text-lg shadow-lg">
                            {shop.shopName.charAt(0).toUpperCase()}
                        </div>
                        {shop.shopName}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-6">
                        {shop.welcomeMessage || "Bienvenue dans notre boutique en ligne. Découvrez nos meilleurs produits sélectionnés pour vous avec soin."}
                    </p>
                    <div className="space-y-3 text-sm text-gray-600 font-medium">
                        {shop.address && <p className="flex items-center"><span className="w-8 h-8 rounded-full bg-white flex items-center justify-center mr-3 shadow-sm text-gray-400"><FiMapPin /></span> {shop.address}</p>}
                        {shop.phone && <p className="flex items-center"><span className="w-8 h-8 rounded-full bg-white flex items-center justify-center mr-3 shadow-sm text-gray-400"><FiPhone /></span> {shop.phone}</p>}
                    </div>
                </div>

                {/* Quick Links (Categories) */}
                {categoryChunks.map((chunk, idx) => (
                    <div key={idx} className="lg:col-span-1">
                        <h4 className="font-bold text-gray-900 mb-6 text-xs uppercase tracking-widest border-b border-gray-200 pb-2 inline-block">
                            {idx === 0 ? 'Catégories' : '\u00A0'}
                        </h4>
                        <ul className="space-y-3 text-sm text-gray-600">
                            {chunk.map(cat => (
                                <li key={cat}>
                                    <button onClick={() => { setSelectedCategory(cat); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="hover:text-brand-blue transition-colors flex items-center group w-full">
                                        <span className="w-1.5 h-1.5 bg-gray-300 rounded-full mr-3 group-hover:bg-brand-blue group-hover:scale-150 transition-all"></span>
                                        {cat}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}

                {/* Socials & Newsletter */}
                <div className="lg:col-span-1">
                    <h4 className="font-bold text-gray-900 mb-6 text-xs uppercase tracking-widest border-b border-gray-200 pb-2 inline-block">Suivez-nous</h4>
                    <div className="flex gap-4 mb-8">
                        {shop.facebookLink ? (
                            <a href={shop.facebookLink} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-gray-600 hover:bg-[#1877F2] hover:text-white transition-all shadow-sm hover:shadow-md hover:-translate-y-1 border border-gray-100"><FiFacebook size={20} /></a>
                        ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-300 cursor-not-allowed border border-gray-100" title="Non disponible"><FiFacebook size={20} /></div>
                        )}
                        
                        {shop.instagramLink ? (
                            <a href={shop.instagramLink} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-gray-600 hover:bg-[#E4405F] hover:text-white transition-all shadow-sm hover:shadow-md hover:-translate-y-1 border border-gray-100"><FiInstagram size={20} /></a>
                        ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-300 cursor-not-allowed border border-gray-100" title="Non disponible"><FiInstagram size={20} /></div>
                        )}

                        {shop.websiteLink ? (
                            <a href={shop.websiteLink} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-900 hover:text-white transition-all shadow-sm hover:shadow-md hover:-translate-y-1 border border-gray-100"><FiGlobe size={20} /></a>
                        ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-300 cursor-not-allowed border border-gray-100" title="Non disponible"><FiGlobe size={20} /></div>
                        )}
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed">
                        © {new Date().getFullYear()} {shop.shopName}. <br/>Tous droits réservés. <br/>Propulsé par <Link to="/" className="font-bold text-gray-500 hover:text-brand-blue transition-colors">Jaapand-e</Link>
                    </p>
                </div>
            </div>
            
        </div>
      </footer>
    </div>
  );
};

export default PublicShop;
